import React, { useEffect, useState } from 'react';
import { Table, Button, Space, message, Input, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useUser } from '../../contexts/UserContext';
import { useBots } from '../../hooks/useWpbot';
import useAuth from '../../hooks/useAuth';
import wpbotService from '../../services/wpbotService';
import { TBot } from '../../models/wpbotModel';
import { contentPath, modules } from '../../config/pages';

const ViewBotsList: React.FC = () => {
  const { config } = useConfig();
  const { company } = useCompany();
  const { user } = useUser();
  const history = useHistory();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [searchText, setSearchText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, error, mutate } = useBots(tokenData, { company: company.current?.uid });

  useEffect(() => {
    if (error) {
      message.error("Error al cargar los bots");
    }
  }, [error]);

  const canCreate = user.permissions.actions.join().includes('wpbot.action.bot.create')
  const canUpdate = user.permissions.actions.join().includes('wpbot.action.bot.update')
  const canDelete = user.permissions.actions.join().includes('wpbot.action.bot.toggle_status')

  const handleDelete = async (bot: TBot) => {
    setDeleting(true)
    const res = await wpbotService.deleteBot(tokenData, { id: bot.id, company: company.current?.uid })
    setDeleting(false)
    if (res?.error) {
      message.error(res.message)
      return
    }
    message.success("Bot eliminado")
    mutate()
  }

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: 'Teléfono',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      // PENDIENTE: elegir modelo por bot. Hoy el driver expone OPENROUTER_MODEL.
      title: 'Modelo',
      dataIndex: 'model',
      key: 'model',
      render: (val: string) => val || '—'
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Activo' : 'Inactivo'}</Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: TBot) => (
        <Space size="middle">
          {canUpdate && (
            <Button 
              type="primary" 
              ghost 
              icon={<EditOutlined />} 
              onClick={() => history.push(contentPath[modules.BotDetail].replace(':id', record.id))}
            >
              Ver/Editar
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="¿Eliminar este bot? Se desactivará y dejará de responder."
              okText="Eliminar"
              cancelText="Cancelar"
              onConfirm={() => handleDelete(record)}
            >
              <Button danger icon={<DeleteOutlined />} loading={deleting}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const bots: TBot[] = data?.status === 200 ? (data?.bots || []) : []

  const filteredData = bots.filter((bot) =>
    bot.display_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    bot.phone_number?.toLowerCase().includes(searchText.toLowerCase()) ||
    bot.model?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Bots de WhatsApp</h2>
        {canCreate && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => history.push(contentPath[modules.BotCreate])}
          >
            Nuevo Bot
          </Button>
        )}
      </div>
      
      <div style={{ marginBottom: '16px', maxWidth: '400px' }}>
        <Input 
          placeholder="Buscar bot (nombre, teléfono, modelo)..." 
          prefix={<SearchOutlined />} 
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </div>

      <Table 
        columns={columns} 
        dataSource={filteredData} 
        rowKey="id" 
        loading={isLoading}
        locale={{ emptyText: "No hay bots configurados para esta compañía" }}
      />
    </div>
  );
};

export default ViewBotsList;
